import React, { useRef, useState } from "react";
import "./modal_list_data_item_location.css";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from 'swr';
import axios from 'axios';

interface DataType {
    key: React.Key;
    id: number;
    item_code: string;
    item_name: string;
    location_code_rack: string;
    location_name_rack: string;
}

interface ModalListDataItemLocationProps {
    onRowSelect: (record: DataType) => void;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

const ModalListDataItemLocation: React.FC<ModalListDataItemLocationProps> = ({ onRowSelect }) => {
    const transformStyle = {
    transform: "scale(0.80)",
    transformOrigin: "top left",
    width: "125%",
};

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const { data, error } = useSWR<DataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/item-locations`, fetcher);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase())
                : false,
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
            sorter: (a, b) => a.item_code.localeCompare(b.item_code),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('item_code'),
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            sorter: (a, b) => a.item_name.localeCompare(b.item_name),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('item_name'),
        },
        {
            title: 'Item Code Location',
            dataIndex: 'location_code_rack',
            key: 'location_code_rack',
            sorter: (a, b) => (a.location_code_rack || '').localeCompare(b.location_code_rack || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('location_code_rack'),
        },
        {
            title: 'Item Name Location',
            dataIndex: 'location_name_rack',
            key: 'location_name_rack',
            sorter: (a, b) => (a.location_name_rack || '').localeCompare(b.location_name_rack || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('location_name_rack'),
        },
    ];

    if (error) return <div>Failed to load data.</div>;

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="izcg">
                <div className="gjs-grid-column" id="i402">
                    <Table<DataType>
                        columns={columns}
                        dataSource={data}
                        loading={!data}
                        onRow={(record) => ({
                            onClick: () => {
                                onRowSelect(record);
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModalListDataItemLocation;